/**
 * AnalysisProgressDisplay Component
 *
 * Reusable progress display for document analysis.
 * Shows progress bar, step counter, stats, and elapsed time.
 *
 * Used by:
 * - UnifiedDocumentUploader (Canvas)
 * - SourceUploader (Sources)
 */

import {
  Loader2,
  AlertCircle,
  CheckCircle,
  FileText,
  Sparkles,
  Clock,
  BarChart3,
} from 'lucide-react'
import type { AnalysisProgress } from '@/hooks/useDocumentAnalyzer'

interface AnalysisProgressDisplayProps {
  progress: AnalysisProgress
  /** Optional className for the container */
  className?: string
}

// Format elapsed time
function formatTime(ms: number) {
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

export function AnalysisProgressDisplay({ progress, className = '' }: AnalysisProgressDisplayProps) {
  if (progress.stage === 'idle') return null

  const isError = progress.stage === 'error'
  const isComplete = progress.stage === 'complete'
  const isProcessing = !isError && !isComplete

  return (
    <div
      className={`rounded-lg border p-4 ${
        isError
          ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
          : isComplete
          ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
          : 'bg-warm-50 border-warm-200 dark:bg-zinc-800 dark:border-zinc-700'
      } ${className}`}
    >
      {/* Header with status */}
      <div className="flex items-start gap-3">
        {isError ? (
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        ) : isComplete ? (
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        ) : (
          <Loader2 className="h-5 w-5 text-solution animate-spin flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          {/* Main message */}
          <p
            className={`text-sm font-medium ${
              isError
                ? 'text-red-700 dark:text-red-300'
                : isComplete
                ? 'text-green-700 dark:text-green-300'
                : 'text-text-primary dark:text-zinc-100'
            }`}
          >
            {progress.message}
          </p>

          {/* Sub-progress (page by page) */}
          {isProcessing && progress.subProgress && (
            <p className="text-xs text-solution dark:text-blue-400 mt-1">
              {progress.subProgress.label}
            </p>
          )}

          {/* Results as they come in */}
          {isProcessing && progress.facetsFound > 0 && (
            <p className="text-xs text-text-secondary dark:text-zinc-400 mt-1">
              Found {progress.facetsFound} insights
              {progress.visualFacetsFound > 0 && ` + ${progress.visualFacetsFound} from visuals`}
            </p>
          )}

          {/* Final stats on complete */}
          {isComplete && (
            <div className="flex items-center gap-3 mt-1 text-xs text-green-600 dark:text-green-400">
              {progress.facetsFound > 0 && (
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  {progress.facetsFound + progress.visualFacetsFound} insights
                </span>
              )}
              {progress.stats.wordCount > 0 && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {progress.stats.wordCount.toLocaleString()} words
                </span>
              )}
              {progress.elapsedMs > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatTime(progress.elapsedMs)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Step counter */}
        {isProcessing && progress.totalSteps > 0 && (
          <div className="text-xs text-text-tertiary dark:text-zinc-400 bg-white dark:bg-zinc-700 px-2 py-1 rounded-full border border-warm-200 dark:border-zinc-600 flex-shrink-0">
            Step {progress.currentStep}/{progress.totalSteps}
          </div>
        )}
      </div>

      {/* Progress bar */}
      {isProcessing && (
        <div className="mt-3 space-y-1">
          <div className="h-2 bg-warm-200 dark:bg-zinc-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-solution transition-all duration-300 ease-out"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-text-tertiary dark:text-zinc-400">
            <span>{progress.percentage}%</span>
            {progress.stats.wordCount > 0 && (
              <span className="flex items-center gap-1">
                <BarChart3 className="h-3 w-3" />
                {progress.stats.wordCount.toLocaleString()} words
                {progress.stats.pageCount > 0 && ` · ${progress.stats.pageCount} pages`}
              </span>
            )}
            {progress.elapsedMs > 1000 && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatTime(progress.elapsedMs)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Step indicators (dots) */}
      {isProcessing && progress.totalSteps > 0 && (
        <div className="mt-3 flex justify-center gap-1.5">
          {Array.from({ length: progress.totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 w-1.5 rounded-full transition-colors ${
                i < progress.currentStep
                  ? 'bg-solution'
                  : i === progress.currentStep
                  ? 'bg-solution/50 animate-pulse'
                  : 'bg-warm-300 dark:bg-zinc-600'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
